import { useMemo } from 'react';
import { useMeQuery } from '@/api/user';
import { InvitationData } from '@/types';

const useMeInvitations = (): {
  data: InvitationData[];
  isLoading: boolean;
} => {
  const { data: me, isLoading } = useMeQuery();

  const invitations: InvitationData[] = useMemo(() => {
    if (!isLoading && me) {
      return [
        // Legacy friend pending memberships.
        ...me.memberships
          .filter(
            ({ status, invitationExpired }) =>
              status === 'pending' && !invitationExpired,
          )
          .map((membership): InvitationData => ({
            namespace: 'organisation',
            source: 'friend',
            token: membership.invitationToken ?? '',
            organisationName: membership.organisationName,
            avatar: membership.organisationAvatar,
            invitedBy: membership.inviterName ?? '',
            expiresAt: membership.invitationExpiresAt ?? '',
            canBeAccepted: membership.invitationCanBeAccepted,
          })),
        // Diplomat rows, both kinds. Whether an organisation accept sticks
        // (e.g. an SSO-mandating organisation) is answered at accept time,
        // not here.
        ...(me.invitations ?? []).map((invitation): InvitationData =>
          invitation.namespace === 'organisation'
            ? {
                namespace: 'organisation',
                source: 'diplomat',
                token: invitation.token,
                organisationName: invitation.label,
                avatar: invitation.ownerAvatarUrl ?? undefined,
                invitedBy: invitation.issuerName,
                expiresAt: invitation.expiresAt,
                canBeAccepted: true,
              }
            : {
                namespace: 'disclosure',
                token: invitation.token,
                objectKey: invitation.objectKey,
                label: invitation.label,
                sharedBy: invitation.issuerName,
                expiresAt: invitation.expiresAt,
                ownerName: invitation.ownerName ?? undefined,
                avatar: invitation.ownerAvatarUrl ?? undefined,
              },
        ),
      ];
    } else {
      return [];
    }
  }, [me, isLoading]);

  const result = useMemo(
    () => ({
      data: invitations,
      isLoading: isLoading,
    }),
    [invitations, isLoading],
  );

  return result;
};

export { useMeInvitations };
